import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { BRAND, NAV_LINKS } from '../data/content';

const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <motion.header
            data-testid="site-header"
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? 'bg-[#0A0A0B]/85 backdrop-blur-xl border-b border-white/10'
                    : 'bg-transparent border-b border-transparent'
            }`}
        >
            <div className="mx-auto max-w-[1500px] px-6 md:px-10 h-[72px] flex items-center justify-between">
                <a href="#top" data-testid="brand-link" className="flex items-center gap-3 group">
                    <span className="h-8 w-8 grid place-items-center border border-white/40 text-white font-mono text-[11px] tracking-widest group-hover:border-[#FF3B00] group-hover:text-[#FF3B00] transition-colors">
                        IID
                    </span>
                    <span className="hidden sm:block text-white text-[13px] font-mono uppercase tracking-[0.22em]">
                        Indian Industrial<span className="text-white/50"> · </span>Distributors
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-testid={`nav-${l.id}`}
                            className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/70 hover:text-white transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="#contact"
                        data-testid="header-cta"
                        className="hidden md:inline-flex items-center gap-2 h-10 px-5 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:bg-white hover:text-[#0A0A0B] transition-colors"
                    >
                        Get Quote →
                    </a>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        className="lg:hidden h-10 w-10 grid place-items-center text-white border border-white/20"
                        aria-label="menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-[#0A0A0B] border-t border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {NAV_LINKS.map((l) => (
                                <a
                                    key={l.id}
                                    href={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-${l.id}`}
                                    className="text-white text-lg font-display uppercase tracking-tight"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="#contact"
                                onClick={() => setOpen(false)}
                                data-testid="mobile-header-cta"
                                className="mt-3 inline-flex justify-center items-center h-11 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.24em]"
                            >
                                Get Quote →
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Header;
